import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import {
  usersService,
  type UserFilters,
  type UserCreateInput,
  type UserUpdateInput,
  type UserRoleAssignment,
  type UserAccessAssignment,
  type PaginatedUsersResult,
} from '../api';
import type { User } from '@lognai/core-platform';
import { UserStatus } from '../types/domain-enums';
import { queryKeys } from './query-keys';

/**
 * Hook to fetch users list. Server-paginated: returns the full envelope so
 * the page can render `total`/`totalPages` without re-counting. Pass
 * `filters.page` / `filters.limit` to advance pages.
 */
export function useUsers(
  filters?: UserFilters,
  options?: Omit<UseQueryOptions<PaginatedUsersResult>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.users.list(filters || {}),
    queryFn: () => usersService.list(filters),
    ...options,
  });
}

/**
 * Hook to fetch a single user
 */
export function useUser(
  userId: string | undefined,
  options?: Omit<UseQueryOptions<User>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.users.detail(userId || ''),
    queryFn: () => {
      if (!userId) throw new Error('User ID is required');
      return usersService.getById(userId);
    },
    enabled: !!userId,
    ...options,
  });
}

/**
 * Hook to fetch user's roles
 */
export function useUserRoles(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.users.roles(userId || ''),
    queryFn: () => {
      if (!userId) throw new Error('User ID is required');
      return usersService.getRoles(userId);
    },
    enabled: !!userId,
  });
}

/**
 * Hook to fetch user's access assignments
 */
export function useUserAccessAssignments(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.users.accessAssignments(userId || ''),
    queryFn: () => {
      if (!userId) throw new Error('User ID is required');
      return usersService.getAccessAssignments(userId);
    },
    enabled: !!userId,
  });
}

/**
 * Hook to fetch user's effective permissions
 */
export function useUserPermissions(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.users.permissions(userId || ''),
    queryFn: () => {
      if (!userId) throw new Error('User ID is required');
      return usersService.getEffectivePermissions(userId);
    },
    enabled: !!userId,
  });
}

/**
 * Hook to create a user
 */
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UserCreateInput) => usersService.create(data),
    // The result carries a one-time set-password link: never keep it cached.
    gcTime: 0,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
  });
}

/**
 * Hook to update a user
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UserUpdateInput }) =>
      usersService.update(userId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
  });
}

/**
 * Hook to delete a user
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => usersService.delete(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
  });
}

/**
 * Hook to update user status
 */
export function useUpdateUserStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, status }: { userId: string; status: UserStatus }) =>
      usersService.updateStatus(userId, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
  });
}

/**
 * Hook to assign role to user
 */
export function useAssignUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, assignment }: { userId: string; assignment: UserRoleAssignment }) =>
      usersService.assignRole(userId, assignment),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.roles(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.permissions(variables.userId) });
    },
  });
}

/**
 * Hook to remove role from user
 */
export function useRemoveUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      usersService.removeRole(userId, roleId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.roles(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.permissions(variables.userId) });
    },
  });
}

/**
 * Grant or revoke a user's access to a second company.
 *
 * The endpoints have existed all along and nothing in the product called them,
 * while the user form told admins to "use secondary companies for multi-company
 * access" — copy pointing at a control that was never built (QA ledger
 * AUTH-012). It is also the only way to set up the multi-company session the
 * company switcher needs, which is why that path had never been exercised
 * end-to-end (AUTH-014).
 */
export function useAddSecondaryCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, companyId }: { userId: string; companyId: string }) =>
      usersService.addSecondaryCompany(userId, companyId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
  });
}

export function useRemoveSecondaryCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, companyId }: { userId: string; companyId: string }) =>
      usersService.removeSecondaryCompany(userId, companyId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
  });
}

/**
 * Hook to assign direct access to user
 */
export function useAssignUserAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, assignment }: { userId: string; assignment: UserAccessAssignment }) =>
      usersService.assignAccess(userId, assignment),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.accessAssignments(variables.userId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.permissions(variables.userId) });
    },
  });
}

/**
 * Hook to remove direct access from user
 */
export function useRemoveUserAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, accessId }: { userId: string; accessId: string }) =>
      usersService.removeAccess(userId, accessId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.accessAssignments(variables.userId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.permissions(variables.userId) });
    },
  });
}

/**
 * Hook to fetch a user's password-link history (who generated each link,
 * when, and what became of it).
 */
export function usePasswordLinks(
  userId: string | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.users.passwordLinks(userId || ''),
    queryFn: () => {
      if (!userId) throw new Error('User ID is required');
      return usersService.getPasswordLinks(userId);
    },
    enabled: !!userId && (options?.enabled ?? true),
  });
}

/**
 * Hook to generate a one-time password link. Generating supersedes the
 * user's previous link, so the history is refreshed.
 */
export function useGeneratePasswordLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => usersService.generatePasswordLink(userId),
    // The result is the raw one-time token: never keep it cached.
    gcTime: 0,
    onSuccess: (_link, userId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.passwordLinks(userId) });
    },
  });
}

/**
 * Hook to email a user a password reset link
 */
export function useSendPasswordResetLink() {
  return useMutation({
    mutationFn: (email: string) => usersService.sendPasswordResetLink(email),
  });
}

/*
  The MFA hooks that used to sit here are gone with the endpoints behind them.
  They called `POST /users/:id/mfa/enable` with no body, against a server that
  demanded a `secret` in one — and took whatever secret it was handed as the
  account's second factor, without ever asking for a code to prove the user's
  app held it. Enrolment now lives on the auth router, where it is two steps
  and the secret is generated server-side (QA ledger AUTH-010).
*/
